import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { Clock, ChevronRight, ChevronDown, Sparkles, X } from 'lucide-react';
import { Button } from '../components/ui/button';
import { PetalIndicator, STATE_COLORS, STATE_NAMES } from '../components/PetalIndicator';
import { SCENARIOS, SCENARIO_MODES } from '../data/scenarios';

const STATES_INTRO_KEY = 'clarityos_states_intro_seen';

// State descriptions for intro modal and expandable legend
const STATE_DESCRIPTIONS = {
  ground: "Who you are when pressure strips away the performance",
  observe: "Seeing clearly without being captured by what you see",
  connect: "Genuine presence with another person - curiosity over agenda",
  execute: "Decisive movement from commitment, not from fear",
  integrate: "Synthesising complexity into wisdom rather than just conclusions"
};

// Small petal SVG for legend and sequence strips (12x16px)
const SmallPetal = ({ state, className = '' }) => (
  <svg width="12" height="16" viewBox="0 0 12 16" className={className}>
    <path 
      d="M6 1 C3 5, 2 9, 6 15 C10 9, 9 5, 6 1 Z"
      fill={STATE_COLORS[state]}
    />
  </svg>
);

// First-visit intro modal
const StatesIntroModal = ({ onDismiss }) => (
  <div className="fixed inset-0 bg-black/80 backdrop-blur-sm z-50 flex items-center justify-center p-4">
    <motion.div
      initial={{ opacity: 0, scale: 0.95 }}
      animate={{ opacity: 1, scale: 1 }}
      className="bg-clarity-paper rounded-2xl max-w-lg w-full p-6 border border-white/10"
    >
      <div className="text-center mb-6">
        <Sparkles className="w-8 h-8 text-amber-500 mx-auto mb-3" />
        <h2 className="font-heading text-2xl font-bold text-white mb-2">
          The Five Leadership States
        </h2>
        <p className="text-slate-400 text-sm">
          Each scenario guides you through an optimal sequence of these states.
        </p>
      </div>
      
      <div className="space-y-3 mb-6">
        {['ground', 'observe', 'connect', 'execute', 'integrate'].map(state => (
          <div key={state} className="flex items-start gap-3 p-3 rounded-lg bg-clarity-subtle/30">
            <SmallPetal state={state} className="flex-shrink-0 mt-1" />
            <div>
              <p className="font-medium text-white">{STATE_NAMES[state]}</p>
              <p className="text-sm text-slate-400">{STATE_DESCRIPTIONS[state]}</p>
            </div>
          </div>
        ))}
      </div>
      
      <Button
        onClick={onDismiss}
        className="w-full bg-amber-500 text-slate-900 hover:bg-amber-400 font-bold py-3"
        data-testid="states-intro-begin-btn"
      >
        Begin
      </Button>
    </motion.div>
  </div>
);

// Expandable legend component
const ExpandableLegend = ({ isExpanded, onToggle }) => (
  <div className="glass rounded-xl overflow-hidden mb-8">
    <button 
      onClick={onToggle}
      className="w-full p-4 flex items-center justify-between hover:bg-white/5 transition-colors"
      data-testid="legend-toggle-btn"
    >
      <div className="flex items-center gap-2">
        <p className="text-xs text-slate-500 uppercase tracking-wider">State Legend</p>
        <div className="flex items-center gap-4 ml-4">
          {['ground', 'observe', 'connect', 'execute', 'integrate'].map(state => (
            <div key={state} className="flex items-center gap-1.5">
              <SmallPetal state={state} />
              <span className="text-sm text-slate-300">{STATE_NAMES[state]}</span>
            </div>
          ))}
        </div>
      </div>
      <ChevronDown className={`w-5 h-5 text-slate-400 transition-transform ${isExpanded ? 'rotate-180' : ''}`} />
    </button>
    
    <AnimatePresence>
      {isExpanded && (
        <motion.div
          initial={{ height: 0, opacity: 0 }}
          animate={{ height: 'auto', opacity: 1 }}
          exit={{ height: 0, opacity: 0 }}
          className="border-t border-white/10"
        >
          <div className="p-4 space-y-3">
            {['ground', 'observe', 'connect', 'execute', 'integrate'].map(state => (
              <div key={state} className="flex items-start gap-3">
                <SmallPetal state={state} className="flex-shrink-0 mt-1" />
                <div>
                  <p className="font-medium text-white">{STATE_NAMES[state]}</p>
                  <p className="text-sm text-slate-400">{STATE_DESCRIPTIONS[state]}</p>
                </div>
              </div>
            ))}
          </div>
        </motion.div>
      )}
    </AnimatePresence>
  </div>
);

export const ScenarioLab = ({ user }) => {
  const navigate = useNavigate();
  const [selectedScenario, setSelectedScenario] = useState(null);
  const [showIntroModal, setShowIntroModal] = useState(false);
  const [legendExpanded, setLegendExpanded] = useState(false);

  // Check if first visit
  useEffect(() => {
    const seen = localStorage.getItem(STATES_INTRO_KEY);
    if (!seen) {
      setShowIntroModal(true);
    }
  }, []);

  const handleDismissIntro = () => {
    localStorage.setItem(STATES_INTRO_KEY, 'true');
    setShowIntroModal(false);
  };

  const handleStartScenario = (scenarioId, mode) => {
    navigate(`/scenario/${scenarioId}?mode=${mode}`);
  };

  return (
    <div className="min-h-screen bg-clarity-bg py-6 px-4">
      {/* First-visit intro modal */}
      {showIntroModal && <StatesIntroModal onDismiss={handleDismissIntro} />}

      <div className="max-w-6xl mx-auto">
        {/* Header */}
        <div className="mb-8">
          <div className="flex items-center gap-2 text-amber-500 text-sm uppercase tracking-widest mb-2">
            <Sparkles className="w-4 h-4" />
            <span>Scenario Lab</span>
          </div>
          <h1 className="font-heading text-3xl md:text-4xl font-bold text-white mb-2">
            Leadership Scenarios
          </h1>
          <p className="text-slate-400 max-w-2xl">
            Train your state fluidity through scenario-based protocols. Each scenario guides you through 
            the optimal state sequence for high-stakes leadership moments.
          </p>
        </div>

        {/* Expandable State Legend */}
        <ExpandableLegend 
          isExpanded={legendExpanded} 
          onToggle={() => setLegendExpanded(!legendExpanded)} 
        />

        {/* Scenario Grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {SCENARIOS.map((scenario, index) => (
            <motion.div
              key={scenario.id}
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: index * 0.1 }}
              className={`glass rounded-xl overflow-hidden border transition-all cursor-pointer group ${
                selectedScenario === scenario.id ? 'border-amber-500/50' : 'border-transparent hover:border-white/20'
              }`}
              onClick={() => setSelectedScenario(selectedScenario === scenario.id ? null : scenario.id)}
              data-testid={`scenario-card-${scenario.id}`}
            >
              {/* Card Header - Always visible */}
              <div className="p-5">
                <h3 className="font-heading text-lg font-bold text-white mb-1 group-hover:text-amber-500 transition-colors">
                  {scenario.name}
                </h3>
                <p className="text-sm text-slate-400 mb-4">
                  {scenario.description}
                </p>

                {/* State Sequence with Petal SVGs */}
                <div className="flex items-center gap-1 mb-4">
                  {scenario.stateSequence.map((state, i) => (
                    <div key={`${state}-${i}`} className="flex items-center">
                      <SmallPetal state={state} />
                      {i < scenario.stateSequence.length - 1 && (
                        <svg width="16" height="8" viewBox="0 0 16 8" className="mx-0.5">
                          <path 
                            d="M0 4 L10 4 M8 2 L12 4 L8 6" 
                            stroke="#64748b"
                            strokeWidth="1"
                            fill="none"
                            strokeLinecap="round"
                          />
                        </svg>
                      )}
                    </div>
                  ))}
                </div>

                {/* Duration Badge */}
                <div className="flex items-center gap-2 text-slate-500 text-sm">
                  <Clock className="w-4 h-4" />
                  <span>~{scenario.duration} min</span>
                </div>
              </div>

              {/* Expanded Section */}
              <AnimatePresence>
                {selectedScenario === scenario.id && (
                  <motion.div
                    initial={{ height: 0, opacity: 0 }}
                    animate={{ height: 'auto', opacity: 1 }}
                    exit={{ height: 0, opacity: 0 }}
                    className="border-t border-white/10"
                  >
                    {/* Fluidity Challenge */}
                    <div className="p-4 bg-amber-500/5 border-l-4 border-amber-500">
                      <p className="text-xs text-amber-500 uppercase tracking-wider mb-1">Key Challenge</p>
                      <p className="text-sm text-slate-300 italic">{scenario.fluidityChallenge}</p>
                    </div>

                    {/* Phase Rows */}
                    <div className="p-4 space-y-2">
                      {scenario.phases.map((phase, phaseIndex) => (
                        <div key={phaseIndex} className="flex items-center gap-3 text-sm">
                          <span className="text-slate-500 w-6">P{phaseIndex + 1}</span>
                          <SmallPetal state={phase.state} />
                          <span style={{ color: STATE_COLORS[phase.state] }} className="font-medium flex-1">
                            {STATE_NAMES[phase.state]}
                          </span>
                          <span className="text-slate-500">{Math.floor(phase.duration / 60)}:{(phase.duration % 60).toString().padStart(2, '0')}</span>
                        </div>
                      ))}
                    </div>

                    {/* Mode Buttons */}
                    <div className="p-4 pt-2 flex gap-3">
                      <Button
                        onClick={(e) => {
                          e.stopPropagation();
                          handleStartScenario(scenario.id, 'prepare');
                        }}
                        className="flex-1 bg-amber-500 text-slate-900 hover:bg-amber-400 font-bold"
                        data-testid={`prepare-btn-${scenario.id}`}
                      >
                        Prepare →
                      </Button>
                      <Button
                        onClick={(e) => {
                          e.stopPropagation();
                          handleStartScenario(scenario.id, 'reflect');
                        }}
                        variant="outline"
                        className="flex-1 border-slate-600 text-slate-300 hover:bg-slate-800"
                        data-testid={`reflect-btn-${scenario.id}`}
                      >
                        Reflect →
                      </Button>
                    </div>
                  </motion.div>
                )}
              </AnimatePresence>

              {/* Expand Indicator - Only when collapsed */}
              {selectedScenario !== scenario.id && (
                <div className="px-5 pb-4">
                  <div className="flex items-center justify-center text-slate-500 text-sm group-hover:text-amber-500 transition-colors">
                    <span>Tap to expand</span>
                    <ChevronRight className="w-4 h-4 ml-1" />
                  </div>
                </div>
              )}
            </motion.div>
          ))}
        </div>

        {/* Info Section */}
        <div className="mt-12 glass rounded-xl p-6">
          <h3 className="font-heading text-lg font-bold text-white mb-3">How Scenario Lab Works</h3>
          <div className="grid md:grid-cols-2 gap-6">
            <div>
              <h4 className="text-amber-500 font-medium mb-2">Prepare Mode</h4>
              <p className="text-sm text-slate-400">
                Use before a real situation. Walk through the optimal state sequence so your nervous 
                system knows the path when stakes are high.
              </p>
            </div>
            <div>
              <h4 className="text-slate-300 font-medium mb-2">Reflect Mode</h4>
              <p className="text-sm text-slate-400">
                Use after a situation that didn't go as intended. Retrace where you fell out of 
                the optimal sequence and encode the correction.
              </p>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ScenarioLab;
